import { useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { isAxiosError } from "axios";
import { usePlanStore } from "../store/planStore";
import { useAuthStore } from "../store/authStore";
import { listPlansRequest } from "../api/plansApi";
import { logoutRequest } from "../api/authApi";
import { Spinner } from "../components/UI/Spinner";

export function PlansPage() {
  const { plans, isLoading, error, setPlans, setLoading, setError } =
    usePlanStore();
  const { user, clear } = useAuthStore();

  const load = useCallback(() => {
    let active = true;
    setLoading(true);
    setError(null);
    listPlansRequest()
      .then((data) => {
        if (active) setPlans(data);
      })
      .catch((e) => {
        if (active) {
          const message = isAxiosError(e)
            ? (e.response?.data as { error?: { message?: string } } | undefined)
                ?.error?.message ?? "Error al cargar planos"
            : "Error inesperado";
          setError(message);
        }
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [setPlans, setLoading, setError]);

  useEffect(() => {
    return load();
  }, [load]);

  async function onLogout() {
    try {
      await logoutRequest();
    } catch {
      // aun si falla el backend, limpiamos local
    } finally {
      clear();
    }
  }

  return (
    <div className="mx-auto flex min-h-full w-full max-w-5xl flex-col gap-6 p-6">
      <header className="flex items-center justify-between border-b border-slate-700 pb-4">
        <div>
          <h1 className="text-2xl font-semibold text-white">Planos</h1>
          {user && (
            <p className="text-sm text-slate-400">
              {user.email} <span className="text-slate-500">({user.role})</span>
            </p>
          )}
        </div>
        <nav className="flex items-center gap-2">
          <Link
            to="/history"
            className="rounded-md border border-slate-600 px-3 py-1.5 text-sm text-slate-300 hover:bg-slate-700/60"
          >
            Historial
          </Link>
          {user?.role === "ADMIN" && (
            <Link
              to="/admin"
              className="rounded-md border border-brand-light bg-brand-light/10 px-3 py-1.5 text-sm text-brand-light hover:bg-brand-light/20"
            >
              Admin
            </Link>
          )}
          <button
            onClick={onLogout}
            className="rounded-md border border-slate-600 px-3 py-1.5 text-sm text-slate-300 hover:bg-slate-700/60"
          >
            Cerrar sesion
          </button>
        </nav>
      </header>

      {isLoading && (
        <div className="flex justify-center py-12">
          <Spinner size={36} />
        </div>
      )}

      {error && (
        <div
          role="alert"
          className="flex items-center justify-between rounded-md border border-red-500/50 bg-red-500/10 px-3 py-2 text-sm text-red-300"
        >
          <span>{error}</span>
          <button
            onClick={load}
            className="ml-3 rounded border border-red-400/40 px-2 py-1 text-xs hover:bg-red-500/20"
          >
            Reintentar
          </button>
        </div>
      )}

      {!isLoading && !error && plans.length === 0 && (
        <p className="text-slate-400">
          No hay planos disponibles. Un administrador debe subir tiles desde
          <code className="mx-1 rounded bg-slate-800 px-1">/admin</code>.
        </p>
      )}

      <ul className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {plans.map((p) => (
          <li
            key={p.id}
            className="rounded-lg border border-slate-700 bg-slate-800/60 p-4 transition hover:border-brand-light"
          >
            <Link to={`/plans/${p.id}`} className="block">
              <h2 className="text-lg font-medium text-white">{p.name}</h2>
              <p className="text-sm text-slate-400">{p.clientName}</p>
              <p className="mt-2 text-xs text-slate-500">
                zoom {p.tileMinZoom}–{p.tileMaxZoom} · {p.tileWidth}×
                {p.tileHeight} px
              </p>
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}
